import { z } from 'zod';
/**
 * Bibliothèque de modèles pré-entraînés (ADR-0018).
 *
 * Contrat de la **fiche métier** : une sélection curée de modèles, présentée en langage opérateur
 * (ce qu'il détecte, sur quelles données, quel gabarit, quels droits), enrichie de métadonnées
 * « vivantes » récupérées d'une plateforme tierce **en lecture seule** (jamais les poids). La
 * récupération est un flux souverain opt-in : quand elle est dormante, `live` reste nul et seule la
 * curation est renvoyée.
 */
/** Type de données capté, qui conditionne le modèle applicable. */
export const MODEL_DATA_TYPES = ['OPTICAL', 'IR', 'ACOUSTIC', 'RADAR'];
export const modelDataTypeSchema = z.enum(MODEL_DATA_TYPES);
/** Plateforme d'hébergement d'origine (l'hébergeur, pas l'auteur du modèle). */
export const LIBRARY_PLATFORMS = ['HUGGINGFACE', 'GITHUB', 'KAGGLE', 'ULTRALYTICS'];
export const libraryPlatformSchema = z.enum(LIBRARY_PLATFORMS);
/** Droit d'usage commercial : autorisé, autorisé sous licence commerciale (ex. Enterprise), interdit. */
export const LIBRARY_COMMERCIAL = ['yes', 'enterprise', 'no'];
export const libraryCommercialSchema = z.enum(LIBRARY_COMMERCIAL);
/**
 * Droits en clair d'un modèle (ADR-0018 §3). Classification **curée** : elle décide de l'éligibilité
 * (commercial + pas de restriction militaire) et alimente la ligne « Droits » de la fiche.
 */
export const libraryLicenseSchema = z.object({
    /** Identifiant SPDX (ex. `Apache-2.0`, `AGPL-3.0`, `MIT`). */
    spdx: z.string(),
    commercial: libraryCommercialSchema,
    militaryAllowed: z.boolean(),
    /** Copyleft réseau (AGPL) : déployer en service impose d'ouvrir le code. */
    copyleftNetwork: z.boolean(),
    attribution: z.boolean(),
});
/** Gabarit opérateur : embarquable sur le drone vs station au sol. */
export const LIBRARY_FORM_FACTORS = ['embedded', 'ground'];
export const libraryFormFactorSchema = z.enum(LIBRARY_FORM_FACTORS);
/**
 * Fiabilité affichée par la pastille. `unevaluated` tant que le modèle n'a pas été évalué sur les
 * données de l'organisation (ADR-0018 §4) : on n'affiche jamais le score du fournisseur comme s'il
 * était le nôtre. Le rattachement aux évaluations viendra dans un incrément ultérieur.
 */
export const LIBRARY_RELIABILITY = ['high', 'medium', 'low', 'unevaluated'];
export const libraryReliabilitySchema = z.enum(LIBRARY_RELIABILITY);
/**
 * Métadonnées « vivantes » récupérées de la plateforme tierce. `null` si la récupération est
 * dormante (opt-in désactivé) ou a échoué — la fiche reste affichable sur la seule curation.
 */
export const libraryLiveSchema = z.object({
    downloads: z.number().int().nonnegative().nullable(),
    /** Dernière mise à jour côté plateforme (ISO 8601). */
    lastModified: z.string().nullable(),
    /** Licence telle que déclarée par la plateforme, pour recoupement avec la classification curée. */
    reportedLicense: z.string().nullable(),
    /** Horodatage de la récupération (ISO 8601). */
    fetchedAt: z.string(),
});
/** Une entrée de la bibliothèque. */
export const modelLibraryItemSchema = z.object({
    id: z.string(),
    /** Nom propre du modèle (non traduit). */
    name: z.string(),
    platform: libraryPlatformSchema,
    /** Lien vers la fiche d'origine (dépôt ou recherche). */
    repoUrl: z.string().url(),
    dataType: modelDataTypeSchema,
    /** Cas d'usage couverts (ids du catalogue de cas d'usage) — sert de « ce qu'il détecte ». */
    useCaseIds: z.array(z.string()),
    formFactor: libraryFormFactorSchema,
    realtime: z.boolean(),
    onnxReady: z.boolean(),
    /** Nombre de paramètres, en clair (détail technique replié). */
    params: z.string().nullable(),
    license: libraryLicenseSchema,
    reliability: libraryReliabilitySchema,
    live: libraryLiveSchema.nullable(),
});
/** Réponse de `GET /model-library`. */
export const modelLibrarySchema = z.object({
    /** La récupération vivante est-elle active ? (opt-in souverain, ADR-0018 §1). */
    liveEnabled: z.boolean(),
    items: z.array(modelLibraryItemSchema),
});
//# sourceMappingURL=model-library.js.map